"use client";

import { useEffect, useState } from "react";

/**
 * Whether this device should be served the cheap version of a decorative
 * animation.
 *
 * Three signals, ORed, because no single one is reliable:
 *
 * - `prefers-reduced-motion` is the stated preference and always wins.
 * - `navigator.hardwareConcurrency` is the only CPU signal Safari exposes.
 *   A 2-core phone cannot afford a per-frame background.
 * - `navigator.deviceMemory` is Chromium-only (so absent on iOS), but on
 *   Android it is the single best predictor of a device that drops frames.
 *
 * Deliberately starts `false` and flips on mount: the server has none of these
 * signals, so any other initial value would hydrate to a mismatch. Decorative
 * animation missing its first frame costs nothing.
 */

type NavigatorWithDeviceHints = Navigator & {
  deviceMemory?: number;
};

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** Below this, a per-frame canvas competes with the page's own work. */
const MIN_CORES = 4;
/** GiB. Chromium clamps the reported value to 0.25–8. */
const MIN_DEVICE_MEMORY = 4;

function isUnderpoweredDevice() {
  if (typeof navigator === "undefined") return false;

  const nav = navigator as NavigatorWithDeviceHints;

  // `hardwareConcurrency` is 0 or undefined where it is not implemented —
  // absence is not evidence of a slow device, so only a real, low reading
  // counts.
  const cores = nav.hardwareConcurrency;
  if (typeof cores === "number" && cores > 0 && cores < MIN_CORES) return true;

  const memory = nav.deviceMemory;
  if (typeof memory === "number" && memory > 0 && memory < MIN_DEVICE_MEMORY)
    return true;

  return false;
}

export function useLowPowerMode(): boolean {
  const [lowPower, setLowPower] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const underpowered = isUnderpoweredDevice();

    const sync = () => setLowPower(query.matches || underpowered);
    sync();

    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  return lowPower;
}
